import type { CatalogEntry, InventoryEntry, MaterialCategory } from '@stageplottter/core';
import { MATERIAL_CATEGORIES, materialCategoryOf } from '@stageplottter/core';
import type { GridColumn, GridEdit, GridValue } from '../../tables/model/types';
import { parseNumber, splitList } from '../../tables/model/values';

type T = (key: string, params?: Record<string, unknown>) => string;

/** Display name of a catalog entry ("Shure SM58"). */
export const catalogName = (e: CatalogEntry): string => `${e.brand} ${e.model}`.trim();

/** Catalog entry whose name is exactly `name` (case-insensitive), if any. */
export function catalogByName(
  catalog: readonly CatalogEntry[],
  name: string,
): CatalogEntry | undefined {
  const n = name.trim().toLowerCase();
  return catalog.find((e) => catalogName(e).toLowerCase() === n);
}

/** Up to 8 catalog models containing the query (brand, model or alias). */
export function suggestCatalog(catalog: readonly CatalogEntry[], query: string) {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  return catalog
    .filter((e) => [catalogName(e), ...e.aliases].some((text) => text.toLowerCase().includes(q)))
    .slice(0, 8)
    .map((e) => ({ value: catalogName(e), label: catalogName(e) }));
}

const isCategory = (v: string): v is MaterialCategory =>
  (MATERIAL_CATEGORIES as readonly string[]).includes(v);

/** Positive whole number (quantity); empty = 0. */
function parseQuantity(text: string): number | undefined {
  const n = parseNumber(text);
  if (n === '') return 0;
  return n !== undefined && Number.isInteger(n) && n >= 0 ? n : undefined;
}

/** Cable length in metres ("10", "10 m", "2,5"); empty = none. */
function parseLength(text: string): number | '' | undefined {
  const n = parseNumber(text.replace(/\s*m$/iu, ''));
  return n === '' || (n !== undefined && n > 0) ? n : undefined;
}

/** Editable columns of the inventory grid (decision U15). */
export function inventoryColumns(
  t: T,
  catalog: readonly CatalogEntry[],
): GridColumn<InventoryEntry>[] {
  return [
    {
      key: 'name',
      header: t('inventory.col.name'),
      type: 'autocomplete',
      width: 240,
      editable: true,
      value: (r) => r.name,
      suggest: (q) => suggestCatalog(catalog, q),
      title: (r) => (r.catalogRef !== undefined ? t('inventory.fromCatalog') : undefined),
    },
    {
      key: 'category',
      header: t('inventory.col.category'),
      type: 'select',
      width: 150,
      editable: true,
      value: (r) => r.category ?? '',
      options: MATERIAL_CATEGORIES.map((c) => ({ value: c, label: t(`materials.category.${c}`) })),
    },
    {
      key: 'quantity',
      header: t('inventory.col.quantity'),
      type: 'number',
      width: 80,
      editable: true,
      align: 'end',
      value: (r) => r.quantity,
      parse: parseQuantity,
    },
    {
      key: 'length',
      header: t('inventory.col.length'),
      type: 'number',
      width: 90,
      editable: true,
      align: 'end',
      value: (r) => r.length ?? '',
      parse: parseLength,
    },
    {
      key: 'serials',
      header: t('inventory.col.serials'),
      type: 'text',
      width: 220,
      editable: true,
      value: (r) => (r.serials ?? []).join(', '),
    },
    {
      key: 'location',
      header: t('inventory.col.location'),
      type: 'text',
      width: 140,
      editable: true,
      value: (r) => r.location ?? '',
    },
    {
      key: 'notes',
      header: t('inventory.col.notes'),
      type: 'text',
      width: 220,
      editable: true,
      value: (r) => r.notes ?? '',
    },
  ];
}

const str = (v: GridValue | undefined): string =>
  typeof v === 'string'
    ? v.trim()
    : Array.isArray(v)
      ? v.join(', ')
      : v === undefined
        ? ''
        : String(v);

/** Sets one column of an entry; empty optional values are removed. */
function setField(
  e: InventoryEntry,
  column: string,
  value: GridValue,
  catalog: readonly CatalogEntry[],
): InventoryEntry {
  const next: InventoryEntry = { ...e };
  const text = str(value);
  switch (column) {
    case 'name': {
      next.name = text === '' ? e.name : text;
      const hit = catalogByName(catalog, next.name);
      if (hit) {
        next.catalogRef = hit.ref;
        if (next.category === undefined) {
          const c = materialCategoryOf({ kind: hit.kind, props: {}, ports: hit.ports });
          if (c !== null) next.category = c;
        }
      } else delete next.catalogRef;
      return next;
    }
    case 'category':
      if (isCategory(text)) next.category = text;
      else delete next.category;
      return next;
    case 'quantity':
      next.quantity = typeof value === 'number' ? value : 0;
      return next;
    case 'length':
      if (typeof value === 'number' && value > 0) next.length = value;
      else delete next.length;
      return next;
    case 'serials': {
      const list = splitList(text);
      if (list.length > 0) next.serials = list;
      else delete next.serials;
      return next;
    }
    case 'location':
    case 'notes':
      if (text !== '') next[column] = text;
      else delete next[column];
      return next;
    default:
      return next;
  }
}

/**
 * Applies a grid edit (cell changes, appended rows from paste / the new-row line). Appended rows
 * without a name are dropped; new entries get ids from `newId`.
 */
export function applyInventoryEdit(
  entries: readonly InventoryEntry[],
  edit: GridEdit,
  catalog: readonly CatalogEntry[],
  newId: () => string,
): InventoryEntry[] {
  const byId = new Map(entries.map((e) => [e.id, e]));
  for (const c of edit.changes) {
    const e = byId.get(c.rowKey);
    if (e) byId.set(e.id, setField(e, c.column, c.value, catalog));
  }
  const out = entries.map((e) => byId.get(e.id)!);
  for (const values of edit.appended) {
    const name = str(values.name);
    if (name === '') continue;
    let e: InventoryEntry = { id: newId(), name, quantity: 1 };
    for (const [column, value] of Object.entries(values)) {
      e = setField(e, column, value, catalog);
    }
    out.push(e);
  }
  return out;
}

/** Inserts an empty entry above / below `rowKey`. */
export function insertInventoryRow(
  entries: readonly InventoryEntry[],
  rowKey: string,
  where: 'above' | 'below',
  newId: () => string,
  name: string,
): InventoryEntry[] {
  const i = entries.findIndex((e) => e.id === rowKey);
  if (i < 0) return [...entries];
  const at = where === 'above' ? i : i + 1;
  return [...entries.slice(0, at), { id: newId(), name, quantity: 0 }, ...entries.slice(at)];
}

export function deleteInventoryRows(
  entries: readonly InventoryEntry[],
  rowKeys: readonly string[],
): InventoryEntry[] {
  const drop = new Set(rowKeys);
  return entries.filter((e) => !drop.has(e.id));
}

/** Total pieces of the inventory. */
export const totalPieces = (entries: readonly InventoryEntry[]): number =>
  entries.reduce((n, e) => n + e.quantity, 0);
